import { useEffect, useState } from "react";
import {
  ArrowRight,
  ChevronLeft,
  Download,
  GalleryHorizontal,
  Image,
  LayoutGrid,
  List,
  Menu,
  MessageCircle,
  Play,
  Search,
  Star,
} from "lucide-react";

interface HorizontalGalleryProps {
  progress: number;
  entrance: number;
}

type MockKind = "text" | "image" | "wide" | "list" | "video" | "commerce" | "feed" | "catalog";

interface MessageProduct {
  name: string;
  detail: string;
  price?: string;
  originalPrice?: string;
  discountPercent?: string;
  reviewCount?: string;
}

interface MessagePromo {
  pill: string;
  headline: string;
  badge: string;
}

interface MessageMock {
  kind: MockKind;
  format: string;
  channel: string;
  headline: string;
  body: string;
  eyebrow: string;
  cta: string;
  color: string;
  accent: string;
  helper?: string;
  promo?: MessagePromo;
  products?: MessageProduct[];
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const KAKAO_BRANDPICK_TITLE = "카카오톡 브랜드픽";
const KAKAO_BRANDPICK_CHANNEL = "(광고) 카카오톡 브랜드픽";

const clampText = (lines: number) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
});

const messageMocks: MessageMock[] = [
  {
    kind: "text",
    format: "기본텍스트형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "신제품 체험 쿠폰",
    headline: "피부 타입에 맞춘 신제품 체험 쿠폰을 받아보세요",
    body: "카카오 추천으로 선별된 뷰티 관심 고객에게 첫 구매 혜택과 체험 신청 경로를 함께 안내합니다.",
    cta: "5,000원 할인 쿠폰",
    color: "tw-from-[#FFF3A5] tw-via-[#FEE500] tw-to-[#FFD06A]",
    accent: "#8A6B00",
    helper: "자세히 보기",
  },
  {
    kind: "image",
    format: "기본텍스트_이미지형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "시즌오프 프로모션",
    headline: "이번 시즌 인기 아이템을 특별가로 확인하세요",
    body: "패션 쇼핑 관심 고객에게 시즌오프 혜택을 이미지와 함께 노출해 즉시 유입을 만듭니다.",
    cta: "5,000원 할인 쿠폰",
    color: "tw-from-[#FFE2F0] tw-via-[#FEC6DB] tw-to-[#FEE500]",
    accent: "#9B3B66",
    helper: "상품 둘러보기",
    promo: {
      pill: "오늘 마감 임박",
      headline: "시즌오프 결산 세일",
      badge: "UP TO 68%",
    },
  },
  {
    kind: "wide",
    format: "와이드 이미지형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "매장 방문 유도",
    headline: "가까운 매장에서 신메뉴 혜택을 받아보세요",
    body: "지역과 외식 관심 신호를 활용해 신메뉴 혜택을 넓은 이미지로 전달합니다.",
    cta: "10% 할인 쿠폰",
    color: "tw-from-[#FFD9A8] tw-via-[#FF9D6C] tw-to-[#FEE500]",
    accent: "#9A4B16",
    helper: "매장 위치 보기",
    promo: {
      pill: "신메뉴 출시 기념",
      headline: "방문 쿠폰이 도착했어요",
      badge: "10% 할인",
    },
  },
  {
    kind: "list",
    format: "와이드 리스트형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "이사/혼수 시즌",
    headline: "새 공간을 준비 중이라면 맞춤 혜택을 확인하세요",
    body: "고관여 상품은 여러 혜택과 상담 경로를 한 번에 비교할 수 있는 리스트형이 적합합니다.",
    cta: "상담 혜택 보기",
    color: "tw-from-[#D6F4FF] tw-via-[#B9E7FF] tw-to-[#C7B8FF]",
    accent: "#375B72",
    helper: "상담 예약하기",
    promo: {
      pill: "이사 시즌 준비",
      headline: "신혼집 필수템 세일",
      badge: "최대 25%",
    },
    products: [
      {
        name: "스타일러 세트",
        detail: "설치+보증 3년",
        price: "1,290,000원",
        originalPrice: "1,690,000원",
        discountPercent: "24%",
      },
      {
        name: "북유럽 원목 침대",
        detail: "매트리스 포함",
        price: "890,000원",
        originalPrice: "1,190,000원",
        discountPercent: "25%",
      },
      {
        name: "혼수 가전 3종",
        detail: "냉장고·세탁기·건조기",
        price: "3,290,000원",
        originalPrice: "3,990,000원",
        discountPercent: "18%",
      },
    ],
  },
  {
    kind: "video",
    format: "프리미엄 동영상형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "체험 수업 모집",
    headline: "이번 달 체험 수업 신청을 열었습니다",
    body: "수업 분위기와 후기를 짧은 영상으로 먼저 보여주고 신청 전환을 유도합니다.",
    cta: "체험 신청하기",
    color: "tw-from-[#CFF7E8] tw-via-[#8FE9CA] tw-to-[#FEE500]",
    accent: "#28775E",
    helper: "수업 후기 보기",
  },
  {
    kind: "commerce",
    format: "캐러셀 커머스형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "시즌 패키지 안내",
    headline: "다가오는 연휴, 추천 패키지를 먼저 확인하세요",
    body: "여러 상품을 카드 형태로 넘겨보게 해 예약 문의 가능성을 높입니다.",
    cta: "패키지 둘러보기",
    color: "tw-from-[#FFE0BD] tw-via-[#FFC078] tw-to-[#FEE500]",
    accent: "#986018",
    helper: "여행 일정 보기",
    products: [
      {
        name: "제주 2박 3일",
        detail: "항공+숙박 포함",
        price: "259,000원~",
        originalPrice: "329,000원~",
        discountPercent: "21%",
      },
      {
        name: "부산 감성 숙소",
        detail: "주말 특가",
        price: "119,000원~",
        originalPrice: "159,000원~",
        discountPercent: "25%",
      },
      {
        name: "일본 온천 패키지",
        detail: "연휴 추천",
        price: "489,000원~",
        originalPrice: "589,000원~",
        discountPercent: "17%",
      },
    ],
  },
  {
    kind: "feed",
    format: "캐러셀 피드형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "지금 가장 핫한 상품",
    headline: "리뷰 좋은 인기 상품을 먼저 보여드려요",
    body: "가격, 할인율, 리뷰를 함께 보여줘 다음 클릭까지 자연스럽게 이어지는 피드형 메시지입니다.",
    cta: "인기 상품 모두 보기",
    color: "tw-from-[#E7F0FF] tw-via-[#CFE3FF] tw-to-[#FEE500]",
    accent: "#33608F",
    helper: "인기 상품 모두 보기",
    products: [
      {
        name: "수분 진정 크림",
        detail: "리뷰 4.9",
        price: "28,000원",
        originalPrice: "39,000원",
        discountPercent: "28%",
        reviewCount: "2,145",
      },
      {
        name: "저자극 선크림",
        detail: "리뷰 4.8",
        price: "19,800원",
        originalPrice: "26,000원",
        discountPercent: "24%",
        reviewCount: "1,532",
      },
      {
        name: "브라이트닝 세럼",
        detail: "리뷰 4.7",
        price: "32,000원",
        originalPrice: "45,000원",
        discountPercent: "29%",
        reviewCount: "980",
      },
    ],
  },
  {
    kind: "catalog",
    format: "카탈로그형",
    channel: KAKAO_BRANDPICK_CHANNEL,
    eyebrow: "이달의 추천 컬렉션",
    headline: "관심 카테고리 인기 상품을 한 번에 모았어요",
    body: "여러 상품을 그리드로 정리해 취향에 맞는 상품을 스스로 고르게 하는 카탈로그형입니다.",
    cta: "전체 컬렉션 보기",
    color: "tw-from-[#FFE7EC] tw-via-[#FFC9D6] tw-to-[#FEE500]",
    accent: "#A23A55",
    helper: "전체 컬렉션 보기",
    products: [
      { name: "미니 크로스백", detail: "3colors", price: "39,000원", discountPercent: "50%" },
      { name: "니트 가디건", detail: "4colors", price: "45,000원", discountPercent: "40%" },
      { name: "와이드 팬츠", detail: "3colors", price: "32,000원", discountPercent: "35%" },
      { name: "체크 머플러", detail: "2colors", price: "18,000원", discountPercent: "45%" },
    ],
  },
];

const TypeIcon = ({ kind }: { kind: MockKind }) => {
  if (kind === "catalog") return <LayoutGrid size={13} />;
  if (kind === "feed") return <GalleryHorizontal size={13} />;
  if (kind === "list" || kind === "commerce") return <List size={13} />;
  if (kind === "video") return <Play size={13} />;
  if (kind === "image" || kind === "wide") return <Image size={13} />;
  return <MessageCircle size={13} />;
};

const ProfileBadge = ({ mock, size = "md" }: { mock: MessageMock; size?: "sm" | "md" }) => (
  <div
    className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br ${mock.color} tw-shadow-sm ${
      size === "sm" ? "tw-h-6 tw-w-6" : "tw-h-8 tw-w-8"
    }`}
  >
    <div className="tw-grid tw-h-[72%] tw-w-[72%] tw-place-items-center tw-rounded-full tw-bg-white/85">
      <div className="tw-flex tw-gap-[3px]">
        <span className="tw-h-[3px] tw-w-[3px] tw-rounded-full tw-bg-[#4A3B00]" />
        <span className="tw-h-[3px] tw-w-[3px] tw-rounded-full tw-bg-[#4A3B00]" />
      </div>
    </div>
  </div>
);

const KakaoActionButtons = ({ mock, isMobile = false }: { mock: MessageMock; isMobile?: boolean }) => (
  <div className={`tw-grid tw-gap-1.5 ${isMobile ? "tw-mt-2" : "tw-mt-3"}`}>
    {!isMobile && (
      <button
        type="button"
        className="tw-h-8 tw-rounded-[4px] tw-bg-[#FEE500] tw-text-[12px] tw-font-semibold tw-text-[#202020]"
      >
        채널 추가
      </button>
    )}
    <button
      type="button"
      className={`tw-rounded-[4px] tw-text-[12px] ${
        isMobile
          ? "tw-h-7 tw-bg-[#FEE500] tw-font-semibold tw-text-[#202020]"
          : "tw-h-8 tw-bg-[#F2F2F0] tw-font-medium tw-text-[#3D3D38]"
      }`}
    >
      {mock.helper ?? "자세히 보기"}
    </button>
  </div>
);

const CouponStrip = ({ mock }: { mock: MessageMock }) => (
  <div className="tw-mt-1.5 tw-flex tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-[#ECECE8] tw-bg-[#FAFAF8]">
    <div className="tw-min-w-0 tw-flex-1 tw-px-3 tw-py-1.5">
      <div className="tw-text-[11px] tw-font-semibold tw-text-[#4A4A44]">{mock.cta}</div>
      <div className="tw-mt-0.5 tw-text-[10px] tw-text-[#8A8A82]">카카오 추천 고객 전용</div>
    </div>
    <div className="tw-grid tw-w-11 tw-place-items-center tw-border-l tw-border-[#ECECE8] tw-bg-[#F6F8FB] tw-text-[#4B8CD8]">
      <Download size={16} />
    </div>
  </div>
);

const ChatBubble = ({
  mock,
  isMobile = false,
  children,
}: {
  mock: MessageMock;
  isMobile?: boolean;
  children: React.ReactNode;
}) => (
  <div className="tw-flex tw-gap-2">
    <ProfileBadge mock={mock} />
    <div className="tw-min-w-0 tw-flex-1">
      <div className="tw-mb-1 tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-medium tw-text-[#506470]">
        <span>{mock.channel}</span>
        <span className="tw-grid tw-h-3 tw-w-3 tw-place-items-center tw-rounded-full tw-bg-[#5A7D8F] tw-text-[8px] tw-text-white">
          ✓
        </span>
      </div>
      <div
        className={`tw-relative tw-rounded-[12px] tw-rounded-tl-[4px] tw-bg-white tw-shadow-sm ${
          isMobile ? "tw-p-2" : "tw-p-3"
        }`}
      >
        {children}
      </div>
      <div className="tw-mt-1 tw-text-right tw-text-[9px] tw-text-[#728690]">오후 12:03</div>
    </div>
  </div>
);

const PromoBanner = ({ mock, className = "" }: { mock: MessageMock; className?: string }) => {
  if (!mock.promo) return null;
  return (
    <div className={`tw-relative tw-z-10 tw-flex tw-h-full tw-flex-col tw-justify-between ${className}`}>
      <span className="tw-inline-flex tw-w-fit tw-items-center tw-rounded-full tw-bg-white/92 tw-px-2.5 tw-py-1 tw-text-[9px] tw-font-bold tw-text-[#2D2D28] tw-shadow-sm">
        {mock.promo.pill}
      </span>
      <div>
        <div
          className="tw-text-[19px] tw-font-extrabold tw-leading-[1.15] tw-text-[#20201C]"
          style={clampText(1)}
        >
          {mock.promo.headline}
        </div>
        <span className="tw-mt-1.5 tw-inline-block tw-rounded-full tw-bg-[#20201C] tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-bold tw-text-white">
          {mock.promo.badge}
        </span>
      </div>
    </div>
  );
};

const DiscountTag = ({ mock, percent }: { mock: MessageMock; percent?: string }) => {
  if (!percent) return null;
  return (
    <span
      className="tw-absolute tw-left-1 tw-top-1 tw-rounded-[4px] tw-px-1.5 tw-py-0.5 tw-text-[9px] tw-font-bold tw-text-white tw-shadow-sm"
      style={{ backgroundColor: mock.accent }}
    >
      {percent}
    </span>
  );
};

const PriceTag = ({
  price,
  originalPrice,
  accent,
  align = "right",
}: {
  price?: string;
  originalPrice?: string;
  accent: string;
  align?: "left" | "right";
}) => (
  <div className={align === "right" ? "tw-shrink-0 tw-text-right" : "tw-text-left"}>
    <div className="tw-text-[11px] tw-font-bold" style={{ color: accent }}>
      {price}
    </div>
    {originalPrice && (
      <div className="tw-text-[9px] tw-text-[#B7B7AE] tw-line-through">{originalPrice}</div>
    )}
  </div>
);

const VisualScene = ({ mock }: { mock: MessageMock }) => (
  <div className={`tw-relative tw-h-full tw-overflow-hidden tw-bg-gradient-to-br ${mock.color}`}>
    <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.6),transparent_38%),radial-gradient(circle_at_82%_80%,rgba(255,255,255,0.32),transparent_36%)]" />
    {mock.promo ? (
      <PromoBanner mock={mock} className="tw-p-3.5" />
    ) : (
      <>
        <div className="tw-absolute tw-left-4 tw-top-4 tw-rounded-full tw-bg-white/88 tw-px-3 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-[#2D2D28]">
          {mock.eyebrow}
        </div>
        <div className="tw-absolute tw-bottom-4 tw-left-5 tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-[20px] tw-bg-white/55 tw-shadow-inner">
          <div className="tw-h-8 tw-w-5 tw-rounded-full tw-bg-[#202020]/10" />
        </div>
        <div className="tw-absolute tw-bottom-5 tw-right-8 tw-grid tw-h-16 tw-w-16 tw-rotate-6 tw-place-items-center tw-rounded-[24px] tw-bg-white/42 tw-shadow-inner">
          <div className="tw-h-8 tw-w-8 tw-rounded-full tw-bg-[#FEE500]/60" />
        </div>
      </>
    )}
  </div>
);

const ProductThumb = ({
  mock,
  index,
  className = "",
}: {
  mock: MessageMock;
  index: number;
  className?: string;
}) => (
  <div
    className={`tw-relative tw-overflow-hidden tw-rounded-md tw-bg-gradient-to-br ${mock.color} ${className}`}
  >
    <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_28%_20%,rgba(255,255,255,0.72),transparent_24%),radial-gradient(circle_at_72%_78%,rgba(255,255,255,0.36),transparent_28%)]" />
    <div
      className="tw-absolute tw-left-1/2 tw-top-1/2 tw-h-[58%] tw-w-[42%] tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-[14px] tw-bg-white/70 tw-shadow-sm"
      style={{ transform: `translate(-50%, -50%) rotate(${index % 2 ? 8 : -7}deg)` }}
    />
    <div className="tw-absolute tw-bottom-2 tw-left-2 tw-h-2 tw-w-8 tw-rounded-full tw-bg-[#202020]/12" />
    <div className="tw-absolute tw-right-2 tw-top-2 tw-h-3 tw-w-3 tw-rounded-full tw-bg-white/78" />
  </div>
);

const TextMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className="tw-rounded-[10px] tw-border tw-border-[#EEE7D5] tw-bg-[#FFFDF4] tw-p-3">
      <div className="tw-text-[12px] tw-font-semibold" style={{ color: mock.accent }}>
        {mock.eyebrow}
      </div>
      <div className="tw-mt-2 tw-text-[15px] tw-font-semibold tw-leading-snug" style={clampText(2)}>
        {mock.headline}
      </div>
      <p
        className="tw-mt-2 tw-text-[12px] tw-leading-relaxed tw-text-[#4D4634]"
        style={clampText(isMobile ? 3 : 5)}
      >
        {mock.body}
      </p>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
    {!isMobile && <CouponStrip mock={mock} />}
  </ChatBubble>
);

const ImageMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className="tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-[#EFEFEB]">
      <div className="tw-h-[104px] md:tw-h-[108px]">
        <VisualScene mock={mock} />
      </div>
      <div className={isMobile ? "tw-p-2" : "tw-p-3"}>
        <div className="tw-text-[15px] tw-font-semibold tw-leading-snug" style={clampText(isMobile ? 1 : 2)}>
          {mock.headline}
        </div>
        <p
          className="tw-mt-1.5 tw-text-[12px] tw-leading-relaxed tw-text-[#4D4634]"
          style={clampText(isMobile ? 1 : 2)}
        >
          {mock.body}
        </p>
      </div>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
    {!isMobile && <CouponStrip mock={mock} />}
  </ChatBubble>
);

const WideImageMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className="tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-[#EFEFEB]">
      <div className={isMobile ? "tw-h-[100px]" : "tw-h-[108px]"}>
        <VisualScene mock={mock} />
      </div>
      <div className={isMobile ? "tw-p-2" : "tw-p-3"}>
        <div className="tw-text-[15px] tw-font-semibold tw-leading-snug" style={clampText(isMobile ? 1 : 2)}>
          {mock.headline}
        </div>
        <p
          className="tw-mt-1.5 tw-text-[12px] tw-leading-relaxed tw-text-[#4D4634]"
          style={clampText(isMobile ? 1 : 2)}
        >
          {mock.body}
        </p>
      </div>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
    {!isMobile && <CouponStrip mock={mock} />}
  </ChatBubble>
);

const ListMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className="tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-[#EFEFEB]">
      <div className={`tw-relative tw-overflow-hidden ${isMobile ? "tw-h-[58px]" : "tw-h-[92px]"} tw-bg-gradient-to-br ${mock.color}`}>
        <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_16%_20%,rgba(255,255,255,0.55),transparent_40%)]" />
        {mock.promo ? (
          <PromoBanner mock={mock} className={isMobile ? "tw-p-2" : "tw-p-3"} />
        ) : (
          <div className={isMobile ? "tw-p-2" : "tw-p-3"}>
            <div className="tw-text-[10px] tw-font-semibold" style={{ color: mock.accent }}>
              {mock.eyebrow}
            </div>
            <div className="tw-mt-1 tw-max-w-[70%] tw-text-[15px] tw-font-semibold tw-leading-tight" style={clampText(isMobile ? 1 : 2)}>
              {mock.headline}
            </div>
          </div>
        )}
      </div>
      <div className={`tw-grid tw-gap-1 ${isMobile ? "tw-p-2" : "tw-gap-1.5 tw-p-3"}`}>
        {(mock.products ?? []).slice(0, isMobile ? 2 : 3).map((item, productIndex) => (
          <div key={item.name} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-[#F7F7F3] tw-p-1.5">
            <div className="tw-relative tw-shrink-0">
              <ProductThumb mock={mock} index={productIndex} className={isMobile ? "tw-h-8 tw-w-8" : "tw-h-9 tw-w-9"} />
              <DiscountTag mock={mock} percent={item.discountPercent} />
            </div>
            <div className="tw-min-w-0 tw-flex-1">
              <div className="tw-truncate tw-text-[12px] tw-font-semibold">{item.name}</div>
              <div className="tw-truncate tw-text-[10px] tw-text-[#777]">{item.detail}</div>
            </div>
            <PriceTag price={item.price} originalPrice={item.originalPrice} accent={mock.accent} />
          </div>
        ))}
      </div>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
  </ChatBubble>
);

const VideoMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className="tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-[#EFEFEB]">
      <div className={`tw-relative ${isMobile ? "tw-h-[95px]" : "tw-h-[154px]"} tw-bg-gradient-to-br ${mock.color}`}>
        <div className="tw-absolute tw-inset-0 tw-bg-[linear-gradient(180deg,rgba(0,0,0,0.02),rgba(0,0,0,0.28))]" />
        <div className="tw-absolute tw-left-5 tw-top-5 tw-h-16 tw-w-24 tw-rounded-lg tw-bg-white/34 tw-shadow-inner" />
        <div className="tw-absolute tw-bottom-9 tw-left-7 tw-h-10 tw-w-10 tw-rounded-full tw-bg-white/42" />
        <div className="tw-absolute tw-bottom-9 tw-right-7 tw-h-12 tw-w-16 tw-rounded-[18px] tw-bg-white/32" />
        <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-grid tw-h-14 tw-w-14 tw--translate-x-1/2 tw--translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-white/90 tw-shadow-lg">
          <Play size={22} fill="#202020" />
        </div>
        <div className="tw-absolute tw-bottom-3 tw-left-3 tw-rounded-[4px] tw-bg-black/55 tw-px-1.5 tw-py-0.5 tw-text-[9px] tw-font-semibold tw-text-white">
          0:38
        </div>
        <div className="tw-absolute tw-bottom-3 tw-left-14 tw-right-3 tw-h-1.5 tw-rounded-full tw-bg-white/40">
          <div className="tw-h-full tw-w-[38%] tw-rounded-full tw-bg-white" />
        </div>
      </div>
      <div className={isMobile ? "tw-p-2" : "tw-p-3"}>
        <div className="tw-text-[15px] tw-font-semibold tw-leading-snug" style={clampText(2)}>
          {mock.headline}
        </div>
        <p className="tw-mt-1.5 tw-text-[12px] tw-leading-relaxed tw-text-[#4D4634]" style={clampText(isMobile ? 1 : 2)}>
          {mock.body}
        </p>
      </div>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
  </ChatBubble>
);

const CommerceMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className="tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-[#EFEFEB]">
      <div
        className={`tw-overflow-hidden ${isMobile ? "tw-h-[42px] tw-p-2" : "tw-h-[72px] tw-p-3"} tw-bg-gradient-to-br ${mock.color}`}
      >
        {!isMobile && (
          <div className="tw-text-[10px] tw-font-semibold" style={{ color: mock.accent }}>
            {mock.eyebrow}
          </div>
        )}
        <div className={`tw-font-semibold tw-leading-tight ${isMobile ? "tw-text-[12px]" : "tw-mt-1 tw-text-[15px]"}`} style={clampText(1)}>
          {mock.headline}
        </div>
      </div>
      <div className={`tw-flex tw-gap-2 tw-overflow-hidden ${isMobile ? "tw-p-2" : "tw-p-3"}`}>
        {(mock.products ?? []).slice(0, 2).map((item, productIndex) => (
          <div
            key={item.name}
            className={`tw-min-w-0 tw-shrink-0 tw-rounded-lg tw-bg-[#F7F7F3] ${isMobile ? "tw-w-[80px] tw-p-1.5" : "tw-w-[84px] tw-p-1.5"}`}
          >
            <div className={isMobile ? "tw-relative tw-mb-1" : "tw-relative tw-mb-2"}>
              <ProductThumb mock={mock} index={productIndex} className={isMobile ? "tw-h-9" : "tw-h-14"} />
              <DiscountTag mock={mock} percent={item.discountPercent} />
            </div>
            <div className="tw-text-[11px] tw-font-semibold tw-leading-tight" style={clampText(isMobile ? 1 : 2)}>
              {item.name}
            </div>
            <div className="tw-mt-1 tw-text-[10px] tw-text-[#777]">{item.detail}</div>
            <div className="tw-mt-1">
              <PriceTag price={item.price} originalPrice={item.originalPrice} accent={mock.accent} align="left" />
            </div>
          </div>
        ))}
      </div>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
  </ChatBubble>
);

const FeedMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className={isMobile ? "tw-mb-1" : "tw-mb-2"}>
      <div className="tw-text-[13px] tw-font-semibold tw-leading-snug" style={clampText(1)}>
        {mock.headline}
      </div>
      <p className="tw-mt-1 tw-text-[11px] tw-leading-relaxed tw-text-[#6B6B62]" style={clampText(isMobile ? 1 : 2)}>
        {mock.body}
      </p>
    </div>
    <div className="tw--mx-1 tw-flex tw-gap-2 tw-overflow-hidden tw-px-1">
      {(mock.products ?? []).slice(0, 2).map((item, productIndex) => (
        <div
          key={item.name}
          className={`tw-min-w-0 tw-shrink-0 tw-rounded-[10px] tw-border tw-border-[#EFEFEB] tw-bg-white tw-p-2 ${isMobile ? "tw-w-[88px]" : "tw-w-[96px]"}`}
        >
          <div className={isMobile ? "tw-relative tw-mb-1" : "tw-relative tw-mb-2"}>
            <ProductThumb mock={mock} index={productIndex} className={isMobile ? "tw-h-12" : "tw-h-16"} />
            <DiscountTag mock={mock} percent={item.discountPercent} />
          </div>
          <div className="tw-truncate tw-text-[11px] tw-font-semibold">{item.name}</div>
          <div className="tw-mt-1">
            <PriceTag price={item.price} originalPrice={item.originalPrice} accent={mock.accent} align="left" />
          </div>
          <div className="tw-mt-1.5 tw-flex tw-items-center tw-justify-between tw-text-[9px] tw-text-[#9A9A90]">
            <span className="tw-flex tw-items-center tw-gap-0.5">
              <Star size={10} fill="#FFC94D" stroke="none" />
              {item.reviewCount}
            </span>
            <ArrowRight size={11} />
          </div>
        </div>
      ))}
    </div>
  </ChatBubble>
);

const CatalogMessage = ({ mock, isMobile }: { mock: MessageMock; isMobile: boolean }) => (
  <ChatBubble mock={mock} isMobile={isMobile}>
    <div className={isMobile ? "tw-rounded-[10px] tw-border tw-border-[#EFEFEB] tw-bg-white tw-p-2" : "tw-rounded-[10px] tw-border tw-border-[#EFEFEB] tw-bg-white tw-p-3"}>
      {!isMobile && (
        <div className="tw-text-[10px] tw-font-semibold" style={{ color: mock.accent }}>
          {mock.eyebrow}
        </div>
      )}
      <div className={`tw-font-semibold tw-leading-snug ${isMobile ? "tw-text-[12px]" : "tw-mt-1 tw-text-[13px]"}`} style={clampText(isMobile ? 1 : 2)}>
        {mock.headline}
      </div>
      <div className={`tw-grid tw-grid-cols-2 tw-gap-2 ${isMobile ? "tw-mt-1.5" : "tw-mt-2.5"}`}>
        {(mock.products ?? []).slice(0, isMobile ? 2 : 4).map((item, productIndex) => (
          <div key={item.name}>
            <div className="tw-relative">
              <ProductThumb mock={mock} index={productIndex} className={isMobile ? "tw-h-12" : "tw-h-14"} />
              <DiscountTag mock={mock} percent={item.discountPercent} />
            </div>
            <div className="tw-mt-1 tw-truncate tw-text-[10px] tw-font-medium">{item.name}</div>
            <PriceTag price={item.price} accent={mock.accent} align="left" />
          </div>
        ))}
      </div>
    </div>
    <KakaoActionButtons mock={mock} isMobile={isMobile} />
  </ChatBubble>
);

const MessagePreview = ({
  mock,
  isMobile,
}: {
  mock: MessageMock;
  isMobile: boolean;
}) => {
  if (mock.kind === "text") return <TextMessage mock={mock} isMobile={isMobile} />;
  if (mock.kind === "wide") return <WideImageMessage mock={mock} isMobile={isMobile} />;
  if (mock.kind === "list") return <ListMessage mock={mock} isMobile={isMobile} />;
  if (mock.kind === "video") return <VideoMessage mock={mock} isMobile={isMobile} />;
  if (mock.kind === "commerce") return <CommerceMessage mock={mock} isMobile={isMobile} />;
  if (mock.kind === "feed") return <FeedMessage mock={mock} isMobile={isMobile} />;
  if (mock.kind === "catalog") return <CatalogMessage mock={mock} isMobile={isMobile} />;
  return <ImageMessage mock={mock} isMobile={isMobile} />;
};

export default function HorizontalGallery({
  progress,
  entrance,
}: HorizontalGalleryProps) {
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1440,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  }));

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = viewport.w < 768;
  const clamped = clamp01(progress);
  const cardBudgetVW = isMobile ? 76 : 26;
  const startVW = isMobile ? 8 : 12;
  const endVW = -(messageMocks.length * cardBudgetVW - (isMobile ? 88 : 70));
  const currentVW = startVW + (endVW - startVW) * clamped;

  // Card height used to be computed two different ways: a JS value (below)
  // just for positioning the strip, and a separate CSS clamp() on the card
  // itself for its actual rendered size. They happened to resolve to the
  // same number most of the time, but neither one checked whether
  // (top clearance + card height) actually fit inside the viewport — on any
  // laptop/browser window shorter than ~960px (desktop) or ~940px (mobile)
  // the card's bottom edge silently overflowed past the visible area and
  // got clipped. Both numbers now come from one calculation that keeps the
  // card inside a "band" between the header clearance and a bottom margin,
  // shrinking the card first instead of ever letting it run past the fold.
  // On short desktop browser windows (a maximized MacBook window is
  // routinely under 850px of usable page height once toolbars are counted),
  // the title block below was still a fixed size and ate into the room the
  // card needs, so cards kept shrinking below what their content needs no
  // matter how the band math was tuned. Letting the title compact itself
  // first frees that room back up before the card ever has to shrink.
  const compactHeader = !isMobile && viewport.h < 820;
  // +26px beyond the title block's own height: the format-label pill on each
  // card is centered on the card's top edge (-translate-y-1/2), so it juts
  // up above where the card "starts" by roughly half its own height. Without
  // this the pill can sit close enough to the title/description above it to
  // visually collide on wide-but-short windows.
  const HEADER_CLEARANCE_PX = isMobile ? 264 : compactHeader ? 236 : 326;
  // Desktop's margin has to fit the "참고용 예시" caption below the cards too
  // (mobile's caption sits further down, clear of the card band already).
  // At the old 24px, the caption's own line-height reached back up into the
  // card's bottom edge whenever a card filled the full band — which desktop
  // does far more often than mobile, since CARD_MAX_PX gives it more room to
  // grow into.
  const BOTTOM_MARGIN_PX = isMobile ? 24 : 44;
  const [CARD_MIN_PX, CARD_MAX_PX, PREFERRED_VH_RATIO] = isMobile
    ? [280, 418, 0.55]
    : [320, 620, 0.64];

  const availableBandPx = Math.max(
    0,
    viewport.h - HEADER_CLEARANCE_PX - BOTTOM_MARGIN_PX
  );
  const cardHeightPx = Math.min(
    CARD_MAX_PX,
    Math.max(CARD_MIN_PX, Math.min(viewport.h * PREFERRED_VH_RATIO, availableBandPx))
  );
  const stripTopPx =
    HEADER_CLEARANCE_PX + Math.max(0, availableBandPx - cardHeightPx) / 2;

  return (
    <div className="tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-bg-[#171717] tw-text-white">
      <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_20%_20%,rgba(254,229,0,0.18),transparent_32%),linear-gradient(180deg,rgba(23,23,23,0.12),#171717_78%)]" />

      <div
        className={`tw-absolute tw-left-6 tw-right-6 tw-top-24 tw-z-20 tw-flex tw-flex-col tw-gap-2 md:tw-left-14 md:tw-right-14 md:tw-flex-row md:tw-items-end md:tw-justify-between md:tw-gap-4 ${
          compactHeader ? "md:tw-top-8" : "md:tw-top-12"
        }`}
        style={{ opacity: entrance }}
      >
        <div>
          <div
            className={`tw-mb-3 tw-flex tw-items-center tw-gap-3 tw-text-[12px] tw-font-semibold tw-text-[#FEE500] md:tw-text-[13px] ${
              compactHeader ? "md:tw-mb-2" : "md:tw-mb-4"
            }`}
          >
            <span className="tw-h-px tw-w-8 tw-bg-[#FEE500]" />
            메시지 유형 샘플
          </div>
          <h2
            className="tw-max-w-[320px] tw-break-keep tw-font-semibold tw-leading-[1.08] tw-tracking-normal tw-text-white md:tw-max-w-3xl"
            style={{
              fontSize: isMobile
                ? "28px"
                : compactHeader
                  ? "36px"
                  : "clamp(32px, 4.4vw, 68px)",
            }}
          >
            브랜드픽은
            <br />
            다양한 형태로 보낼 수 있습니다
          </h2>
        </div>
        {!compactHeader && (
          <p className="tw-hidden tw-max-w-md tw-text-sm tw-leading-relaxed tw-text-white/68 md:tw-block md:tw-text-base">
            캠페인 목적에 맞춰 텍스트, 이미지, 리스트, 동영상, 피드, 카탈로그형 메시지를
            조합해 신규 잠재고객에게 전달할 수 있습니다.
          </p>
        )}
      </div>

      {/*
        The strip's translateX slides cards continuously, so mid-scroll a card
        is always partially past the left or right edge — that's inherent to
        a scroll-linked (not snap-to-card) carousel. Left hard-clipping that
        moment reads as a layout bug (label pill sliced mid-text, price cut
        off). A mask-image edge fade turns the same clipping into an
        intentional "peeking in/out" look instead, without changing the
        scroll mechanic itself.
      */}
      <div
        className="tw-absolute tw-inset-0 tw-overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, black 18%, black 82%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 18%, black 82%, transparent)",
        }}
      >
        <div
          className="tw-absolute tw-flex tw-items-center tw-gap-4 tw-pl-6 tw-pr-6 md:tw-gap-8 md:tw-pl-14 md:tw-pr-14"
          style={{
            top: `${stripTopPx}px`,
            transform: `translateX(calc(${currentVW}vw))`,
            opacity: entrance,
            willChange: "transform, opacity",
          }}
        >
          {messageMocks.map((mock, index) => {
            const parallax =
              (index - (messageMocks.length - 1) / 2) * 8 * clamped;

            return (
              <article
                key={mock.format}
                className="tw-relative tw-shrink-0 tw-overflow-visible tw-text-[#202020]"
                style={{
                  width: isMobile ? "min(70vw, 286px)" : "clamp(260px, 22vw, 332px)",
                  height: `${cardHeightPx}px`,
                  transform: `translateX(${parallax}px)`,
                }}
              >
                <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-30 tw-flex tw--translate-y-1/2 tw-justify-center">
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-border tw-border-white/70 tw-bg-white tw-px-3.5 tw-py-1.5 tw-text-[10px] tw-font-semibold tw-text-[#52626B] tw-shadow-[0_8px_18px_rgba(25,38,45,0.22)]">
                    <TypeIcon kind={mock.kind} />
                    {mock.format}
                  </span>
                </div>
                <div
                  className={`tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-[22px] tw-border tw-border-white/20 tw-bg-[#B9CDD9] tw-shadow-2xl md:tw-p-4 ${
                    isMobile ? "tw-p-2" : "tw-p-3"
                  }`}
                >
                  <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-text-[10px] tw-font-semibold tw-text-[#1D2D36]">
                    <span>9:41</span>
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                      <span className="tw-flex tw-items-end tw-gap-[1.5px]">
                        <span className="tw-h-[3px] tw-w-[2px] tw-rounded-[0.5px] tw-bg-[#1D2D36]" />
                        <span className="tw-h-[4.5px] tw-w-[2px] tw-rounded-[0.5px] tw-bg-[#1D2D36]" />
                        <span className="tw-h-[6px] tw-w-[2px] tw-rounded-[0.5px] tw-bg-[#1D2D36]" />
                        <span className="tw-h-[7.5px] tw-w-[2px] tw-rounded-[0.5px] tw-bg-[#1D2D36]" />
                      </span>
                      <span className="tw-relative tw-flex tw-h-[9px] tw-w-4 tw-items-center tw-rounded-[2px] tw-border tw-border-[#1D2D36] tw-p-[1px]">
                        <span className="tw-h-full tw-w-[70%] tw-rounded-[1px] tw-bg-[#1D2D36]" />
                        <span className="tw-absolute tw--right-[3px] tw-top-1/2 tw-h-[4px] tw-w-[2px] tw--translate-y-1/2 tw-rounded-r-[1px] tw-bg-[#1D2D36]" />
                      </span>
                    </span>
                  </div>

                  <div
                    className={`tw-grid tw-grid-cols-[48px_1fr_48px] tw-items-center tw-text-[#26343B] ${
                      isMobile ? "tw-mb-1" : "tw-mb-2"
                    }`}
                  >
                    <ChevronLeft size={18} />
                    <div className="tw-min-w-0 tw-text-center">
                      <div className="tw-truncate tw-text-[12px] tw-font-semibold tw-leading-tight">
                        {KAKAO_BRANDPICK_TITLE}
                        <span className="tw-ml-1 tw-inline-grid tw-h-3 tw-w-3 tw-place-items-center tw-rounded-full tw-bg-[#5A7D8F] tw-text-[8px] tw-text-white">
                          ✓
                        </span>
                      </div>
                      <div className="tw-text-[9px] tw-text-[#52626B]">1599-5533</div>
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                      <Search size={15} />
                      <Menu size={16} />
                    </div>
                  </div>

                  <div
                    className={`tw-min-h-0 tw-flex-1 tw-overflow-hidden ${
                      isMobile ? "tw-p-1.5" : "tw-p-2"
                    }`}
                  >
                    <div className="tw-min-h-0">
                      <MessagePreview mock={mock} isMobile={isMobile} />
                    </div>

                    <div className={`tw-text-right tw-text-[9px] tw-text-[#5E717C] ${isMobile ? "tw-mt-1" : "tw-mt-2"}`}>
                      읽음 1
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div
        className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-z-20 tw-text-center tw-text-[10px] tw-text-white/40"
        style={{ top: `${stripTopPx + cardHeightPx + 10}px`, opacity: entrance }}
      >
        * 실제 발송 화면은 상품·캠페인에 따라 달라질 수 있는 참고용 예시입니다
      </div>
    </div>
  );
}
